import React from 'react';
import { Card } from './ui/card';
import { mockProcessSteps } from '../utils/mock';
import { ArrowRight } from 'lucide-react';

export const ProcessSection = () => {
  return (
    <section id="process" className="py-20 bg-white relative overflow-hidden">
      {/* Background Pattern */}
      <div 
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1497366754035-f200968a6e72)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      ></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#7C2D12] mb-4">
              Our Trademark Registration Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A streamlined, step-by-step approach to securing your trademark registration with the USPTO. We handle the complexity so you can focus on growing your business.
            </p>
          </div>

          {/* Process Steps */}
          <div className="space-y-8">
            {mockProcessSteps.map((step, index) => (
              <div key={step.step} className="relative">
                <Card className="p-8 bg-[#FFF7ED] border-2 border-[#FED7AA] hover:border-[#F97316] transition-all duration-300 hover:shadow-xl">
                  <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                    {/* Step Number */}
                    <div className="flex-shrink-0">
                      <div className="w-20 h-20 bg-gradient-to-br from-[#7C2D12] to-[#F97316] rounded-2xl flex items-center justify-center shadow-lg">
                        <span className="text-3xl font-bold text-white">
                          {step.step}
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-grow">
                      <h3 className="text-2xl font-bold text-[#7C2D12] mb-3">
                        {step.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed text-lg">
                        {step.description}
                      </p>
                    </div>

                    {/* Arrow Indicator (except for last item) */}
                    {index < mockProcessSteps.length - 1 && (
                      <div className="hidden md:block flex-shrink-0">
                        <ArrowRight className="text-[#F97316]" size={32} />
                      </div>
                    )}
                  </div>
                </Card>

                {/* Mobile Arrow */}
                {index < mockProcessSteps.length - 1 && (
                  <div className="md:hidden flex justify-center my-4">
                    <ArrowRight className="text-[#F97316] rotate-90" size={32} />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Timeline Summary */}
          <div className="mt-16">
            <Card className="p-10 bg-gradient-to-r from-[#7C2D12] to-[#F97316] border-0">
              <h3 className="text-4xl font-bold text-white mb-6 text-center">
                Understanding the Timeline: 8-12 Months Average
              </h3>
              <div className="max-w-4xl mx-auto space-y-4 text-white/95 text-lg leading-relaxed">
                <p>
                  While the USPTO trademark registration process typically takes 8-12 months from initial filing to final registration, this timeline can vary based on several factors including the complexity of your mark, whether office actions are issued, and current USPTO processing times. Some straightforward applications with no issues can register in as little as 6-8 months, while applications that encounter obstacles may take 12-18 months or longer to resolve.
                </p>
                <p>
                  Despite this timeline, filing early is crucial because your filing date establishes your priority—your legal position relative to others who may file for similar marks. We work diligently throughout the process to expedite your application wherever possible, responding promptly to all USPTO communications, addressing office actions quickly and thoroughly, and proactively monitoring your application status. Our goal is to secure your registration as efficiently as possible while ensuring the strongest possible legal protection.
                </p>
                <p className="font-semibold text-[#FED7AA]">
                  Remember: The clock starts ticking on your trademark rights from your filing date, not your registration date. Filing sooner rather than later protects your position and helps prevent competitors from claiming rights to similar marks. Don't delay—start your trademark protection today.
                </p>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
